import type { UseFormSetValue } from 'react-hook-form';
import { CURRENT_USER_LABEL } from './types';
import type { MeetingSlotFormValues } from './types';
import { userIdValue } from './helpers';

type UseMeetingSlotFieldHandlersParams = {
  dealByOption: Map<string, ANY>;
  leadByOption: Map<string, ANY>;
  projectByTitle: Map<string, ANY>;
  setPresenterOpened: (value: boolean) => void;
  setValue: UseFormSetValue<MeetingSlotFormValues>;
  userByLabel: Map<string, ANY>;
};

export function useMeetingSlotFieldHandlers({
  dealByOption,
  leadByOption,
  projectByTitle,
  setPresenterOpened,
  setValue,
  userByLabel,
}: UseMeetingSlotFieldHandlersParams) {
  const handleLeadChange = (value: string) => {
    setValue('leadLabel', value);

    const lead = leadByOption.get(value);
    const id = lead?.id ?? lead?.ID;
    setValue('leadId', typeof id === 'number' ? id : null);

    const ref = lead?.crmRef;
    setValue('leadCrmRef', typeof ref === 'string' ? ref : null);
  };

  const handleLeadOpenChange = (open: boolean) => {
    if (open) {
      setValue('leadQuery', '');
    }
  };

  const handleLeadSearchQueryChange = (query: string) => {
    setValue('leadQuery', query);
    setValue('leadLabel', '');
    setValue('leadId', null);
    setValue('leadCrmRef', null);
  };

  const handleDealChange = (value: string) => {
    setValue('dealLabel', value);

    const deal = dealByOption.get(value);
    const id = deal?.id ?? deal?.ID;
    setValue('dealId', typeof id === 'number' ? id : null);
  };

  const handleDealOpenChange = (open: boolean) => {
    if (open) {
      setValue('dealQuery', '');
    }
  };

  const handleDealSearchQueryChange = (query: string) => {
    setValue('dealQuery', query);
    setValue('dealLabel', '');
    setValue('dealId', null);
  };

  const handleProjectChange = (value: string) => {
    setValue('projectTitle', value);

    const found = projectByTitle.get(value);
    const id = (found?.id ?? found?.ID) as number | undefined;
    setValue('projectId', typeof id === 'number' ? id : null);
  };

  const handleProjectOpenChange = (open: boolean) => {
    if (open) {
      setValue('projectQuery', '');
    }
  };

  const handleProjectSearchQueryChange = (query: string) => {
    setValue('projectQuery', query);
    setValue('projectTitle', query);
    const found = projectByTitle.get(query);
    const id = (found?.id ?? found?.ID) as number | undefined;
    setValue('projectId', typeof id === 'number' ? id : null);
  };

  const handlePresenterChange = (value: string) => {
    setValue('presenterName', value);
    setValue('presenterQuery', value);

    if (value === CURRENT_USER_LABEL) {
      setValue('presenterId', null);
      return;
    }

    const user = userByLabel.get(value);
    setValue('presenterId', userIdValue(user));
  };

  const handlePresenterSearchQueryChange = (query: string) => {
    setPresenterOpened(true);
    setValue('presenterQuery', query);
    setValue('presenterName', query);

    const user = userByLabel.get(query);
    setValue('presenterId', userIdValue(user));
  };

  return {
    handleDealChange,
    handleDealOpenChange,
    handleDealSearchQueryChange,
    handleLeadChange,
    handleLeadOpenChange,
    handleLeadSearchQueryChange,
    handlePresenterChange,
    handlePresenterSearchQueryChange,
    handleProjectChange,
    handleProjectOpenChange,
    handleProjectSearchQueryChange,
  };
}
